import React, { useEffect, useMemo, useState } from 'react';
import { Icon, Select, SelectItem } from './ui';
import * as directory from '../lib/directoryApi';
import { Trans, useTranslation } from 'react-i18next';

/**
 * Policy Bindings view — the binding layer of the unified policy model.
 *
 * A binding attaches an existing policy to a subject scope (agent | group | role
 * | global). The Effective Policy inspector picks an agent and shows the
 * flattened decision with provenance — which binding/scope won, and why
 * (deny-wins / most-specific). `group` is schema-ready but inert in this slice.
 */

type SubjectType = 'agent' | 'group' | 'role' | 'global';

interface PolicyLite {
  policy_id: string;
  name: string;
  effect: 'allow' | 'deny';
}

interface Binding {
  binding_id: string;
  org_id: string;
  policy_id: string;
  subject_type: SubjectType;
  subject_ref: string | null;
  selector: string | null;
  priority: number;
  enabled: number;
  created_at: string;
}

interface AgentLite {
  agent_id: string;
  name: string;
  role: string;
}

interface Provenance {
  binding_id: string;
  subject_type: SubjectType;
  subject_ref: string | null;
  policy_id: string;
  policy_name: string;
}

interface EffectivePolicy {
  agent: { agent_id: string; role: string; org_id: string } | null;
  applicable_bindings: Array<Binding & { policy_name: string; effect: 'allow' | 'deny' }>;
  effective: {
    access: { decision: 'allow' | 'deny' | 'default-deny'; from?: Provenance; note: string };
  };
  fallback: boolean;
}

const SCOPE_LABEL: Record<SubjectType, string> = {
  agent: 'Agent',
  group: 'AD group (owner)',
  role: 'Role',
  global: 'Global (catch-all)',
};

interface Props {
  apiUrl: string;
  policies: PolicyLite[];
}

const PolicyBindings: React.FC<Props> = ({ apiUrl, policies }) => {
  const { t } = useTranslation();
  const [bindings, setBindings] = useState<Binding[]>([]);
  const [agents, setAgents] = useState<AgentLite[]>([]);
  // Synced AD groups; a group binding matches agents whose human owner is a
  // (transitive) member. Stored by SID so a rename doesn't break the binding.
  const [adGroups, setAdGroups] = useState<directory.DirectoryGroup[]>([]);
  useEffect(() => {
    directory.listGroups(apiUrl, '', 500).then(setAdGroups).catch(() => setAdGroups([]));
  }, [apiUrl]);
  const groupName = (sid: string | null) => adGroups.find((g) => g.sid === sid)?.name;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // create-binding form
  const [policyId, setPolicyId] = useState('');
  const [subjectType, setSubjectType] = useState<SubjectType>('global');
  const [subjectRef, setSubjectRef] = useState('');
  const [priority, setPriority] = useState(0);
  const [saving, setSaving] = useState(false);

  // effective-policy inspector
  const [inspectAgent, setInspectAgent] = useState('');
  const [effective, setEffective] = useState<EffectivePolicy | null>(null);
  const [inspecting, setInspecting] = useState(false);

  const policyName = useMemo(() => {
    const m = new Map(policies.map((p) => [p.policy_id, p.name]));
    return (id: string) => m.get(id) || id;
  }, [policies]);

  const fetchBindings = async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/policy-bindings`);
      const data = await res.json();
      setBindings(data.bindings || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load bindings');
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/agents`);
      const data = await res.json();
      setAgents((data.agents || []).map((a: any) => ({ agent_id: a.agent_id, name: a.name, role: a.role })));
    } catch {
      /* agents are optional for the bindings list */
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchBindings(), fetchAgents()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const createBinding = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!policyId) {
      setError('Select a policy to bind');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/admin/policy-bindings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_id: policyId,
          subject_type: subjectType,
          subject_ref: subjectType === 'global' ? null : subjectRef,
          priority,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || d.error || 'Failed to create binding');
      }
      setSubjectRef('');
      setPriority(0);
      await fetchBindings();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteBinding = async (id: string) => {
    await fetch(`${apiUrl}/admin/policy-bindings/${id}`, { method: 'DELETE' });
    await fetchBindings();
    if (effective) await runInspect(inspectAgent);
  };

  const runInspect = async (agentId: string) => {
    if (!agentId) {
      setEffective(null);
      return;
    }
    setInspecting(true);
    try {
      const res = await fetch(`${apiUrl}/admin/agents/${agentId}/effective-policy`);
      setEffective(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setInspecting(false);
    }
  };

  const decisionColor = (d: string) =>
    d === 'allow' ? 'var(--color-success, #2e7d32)' : d === 'deny' ? 'var(--color-danger, #c62828)' : 'var(--color-muted, #888)';

  if (loading) return <p className="muted-text">{t('components.policyBindings.loadingBindings')}</p>;

  return (
    <div className="policy-bindings">
      {error && (
        <div className="notice error">
          <Icon name="warning" /> {error}
        </div>
      )}

      <div className="bindings-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: '1.5rem' }}>
        {/* ---- bindings list + create ---- */}
        <div>
          <h3 style={{ marginTop: 0 }}>
            <Icon name="key" size={16} /> {t('components.policyBindings.bindings')}
          </h3>
          <p className="subtitle">{t('components.policyBindings.attachAPolicyToASubject')}</p>

          <form onSubmit={createBinding} className="binding-form" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end', margin: '0.75rem 0 1rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
              {t('components.policyBindings.policy')}
              <Select value={policyId} onValueChange={setPolicyId} placeholder={t('components.policyBindings.selectPolicy')}>
                {policies.map((p) => (
                  <SelectItem key={p.policy_id} value={p.policy_id}>
                    {p.name} ({p.effect})
                  </SelectItem>
                ))}
              </Select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
              {t('components.policyBindings.scope')}
              <Select value={subjectType} onValueChange={(v) => setSubjectType(v as SubjectType)}>
                <SelectItem value="global">{t('components.policyBindings.globalCatchAll')}</SelectItem>
                <SelectItem value="agent">{t('components.policyBindings.agent')}</SelectItem>
                <SelectItem value="role">{t('components.policyBindings.role')}</SelectItem>
                <SelectItem value="group">{t('components.policyBindings.adGroupAgentOwner')}</SelectItem>
              </Select>
            </label>
            {subjectType !== 'global' && (
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
                {subjectType === 'agent' ? t('components.policyBindings.agent') : subjectType === 'role' ? t('components.policyBindings.role') : t('components.policyBindings.group')}
                {subjectType === 'agent' ? (
                  <Select value={subjectRef} onValueChange={setSubjectRef} placeholder={t('components.policyBindings.selectAgent')}>
                    {agents.map((a) => (
                      <SelectItem key={a.agent_id} value={a.agent_id}>
                        {a.name} ({a.role})
                      </SelectItem>
                    ))}
                  </Select>
                ) : subjectType === 'role' ? (
                  <Select value={subjectRef} onValueChange={setSubjectRef} placeholder={t('components.policyBindings.selectRole')}>
                    <SelectItem value="viewer">{t('components.policyBindings.viewer')}</SelectItem>
                    <SelectItem value="developer">{t('components.policyBindings.developer')}</SelectItem>
                    <SelectItem value="admin">{t('components.policyBindings.admin')}</SelectItem>
                  </Select>
                ) : adGroups.length > 0 ? (
                  <Select value={subjectRef} onValueChange={setSubjectRef} placeholder={t('components.policyBindings.selectADGroup')}>
                    {adGroups.map((g) => (
                      <SelectItem key={g.sid} value={g.sid}>
                        {g.name}
                        {g.memberCount != null ? ` (${g.memberCount})` : ''}
                      </SelectItem>
                    ))}
                  </Select>
                ) : (
                  <input value={subjectRef} onChange={(e) => setSubjectRef(e.target.value)} placeholder={t('components.policyBindings.connectActiveDirectoryFirst')} />
                )}
              </label>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, width: 70 }}>
              {t('components.policyBindings.priority')}
              <input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value) || 0)} />
            </label>
            <button className="ent-btn primary" type="submit" disabled={saving}>
              <Icon name="plus" /> {t('components.policyBindings.bind')}
            </button>
          </form>

          {bindings.length === 0 ? (
            <p className="muted-text">{t('components.policyBindings.noBindingsYetAgentsFallBack')}</p>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>{t('components.policyBindings.policy')}</th>
                  <th>{t('components.policyBindings.scope')}</th>
                  <th>{t('components.policyBindings.subject')}</th>
                  <th>{t('components.policyBindings.prio')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {bindings.map((b) => (
                  <tr key={b.binding_id}>
                    <td>{policyName(b.policy_id)}</td>
                    <td>{SCOPE_LABEL[b.subject_type]}</td>
                    <td>
                      {b.subject_type === 'group' && groupName(b.subject_ref) ? (
                        <span title={b.subject_ref || ''}>{groupName(b.subject_ref)}</span>
                      ) : (
                        <code>{b.subject_ref || '*'}</code>
                      )}
                    </td>
                    <td>{b.priority}</td>
                    <td>
                      <button className="ent-btn ghost" onClick={() => deleteBinding(b.binding_id)} aria-label={t('components.policyBindings.deleteBinding')}>
                        <Icon name="trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ---- effective policy inspector ---- */}
        <div>
          <h3 style={{ marginTop: 0 }}>
            <Icon name="shield" size={16} /> {t('components.policyBindings.effectivePolicy')}
          </h3>
          <p className="subtitle">{t('components.policyBindings.resolveTheMergedDecisionForOne')}</p>

          <select
            value={inspectAgent}
            onChange={(e) => {
              setInspectAgent(e.target.value);
              runInspect(e.target.value);
            }}
            style={{ width: '100%', marginBottom: '1rem' }}
          >
            <option value="">{t('components.policyBindings.selectAnAgent')}</option>
            {agents.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>
                {a.name} ({a.role})
              </option>
            ))}
          </select>

          {inspecting && <p className="muted-text">{t('components.policyBindings.resolving')}</p>}

          {effective && effective.agent && (
            <div className="effective-card" style={{ border: '1px solid var(--color-border, #ddd)', borderRadius: 8, padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <strong>{effective.agent.role}</strong>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: decisionColor(effective.effective.access.decision),
                    textTransform: 'uppercase',
                  }}
                >{t('components.policyBindings.access', { v0: effective.effective.access.decision })}</span>
              </div>

              <p style={{ fontSize: 13, margin: '0 0 12px' }}>{effective.effective.access.note}</p>

              {effective.effective.access.from && (
                <p style={{ fontSize: 12, margin: '0 0 12px' }}>
                  <Trans
                    i18nKey="components.policyBindings.fromBindingPolicy"
                    values={{ scope: SCOPE_LABEL[effective.effective.access.from.subject_type], policy: effective.effective.access.from.policy_name }}
                    components={{ s: <strong />, c: <code /> }}
                  />
                </p>
              )}

              {effective.fallback && (
                <div className="notice" style={{ fontSize: 12 }}>
                  <Icon name="warning" /> {t('components.policyBindings.noBindingsGovernThisAgentLegacy')}
                </div>
              )}

              {effective.applicable_bindings.length > 0 && (
                <>
                  <div style={{ fontSize: 12, color: 'var(--color-muted, #888)', margin: '12px 0 4px' }}>
                    {t('components.policyBindings.applicableBindingsMostSpecificFirst')}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                    {effective.applicable_bindings.map((b) => (
                      <li key={b.binding_id}>
                        {SCOPE_LABEL[b.subject_type]} → <code>{b.policy_name}</code>{' '}
                        <span style={{ color: decisionColor(b.effect) }}>({b.effect})</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyBindings;
